
  import Summary from '../../components/CounterParties/Summary.vue';
  import Connections from '../../components/CounterParties/Connections.vue';
  import Analyze from '../../components/CounterParties/Analyze.vue';
  import Founders from '../../components/CounterParties/Founders.vue';
  import Finances from '../../components/CounterParties/Finances.vue';
  import Arbitration from '@/components/CounterParties/Arbitration.vue';
  import Sanctions from '@/components/CounterParties/Sanctions.vue';
  import Debts from '@/components/CounterParties/Debts.vue';
  import Leasing from '@/components/CounterParties/Leasing.vue';
  import History from '@/components/CounterParties/History.vue';
  import Trademarks from '@/components/CounterParties/Trademarks.vue';
  import Okved from '@/components/CounterParties/Okved.vue';
  import Details from '@/components/CounterParties/Details.vue';
  import Licenses from '@/components/CounterParties/Licenses.vue';
  import Contracts from '@/components/CounterParties/Contracts.vue';

  import { defineComponent, ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue';
  import { useStore } from 'vuex';
  import { useRoute } from 'vue-router';
  import { Modal, Tab } from 'bootstrap';
  import Branches from '@/components/CounterParties/Branches.vue';
  import Inspections from '@/components/CounterParties/Inspections.vue';
  import CompanyService from '@/services/company';
  import type {
    CheckoMessage,
    Company,
    CompanyConnection,
    CompanyFinances,
    Contract,
    Enforcement,
    Inspection,
    LegalCase,
    TimelineEvent,
  } from '@/models/types';
  import * as XLSX from 'xlsx'; // Импортируем библиотеку XLSX

  export default defineComponent({
    name: 'CounterParties',
    components: {
      Summary,
      Connections,
      Analyze,
      Founders,
      Finances,
      Arbitration,
      Contracts,
      Sanctions,
      Licenses,
      Branches,
      Inspections,
      Debts,
      Leasing,
      History,
      Trademarks,
      Okved,
      Details,
    },
    setup() {
      const tabs = [
        { id: 'tab-summary', label: 'Сводка', component: Summary },
        { id: 'tab-arbitration', label: 'Арбитраж', component: Arbitration },
        { id: 'tab-finances', label: 'Финансы', component: Finances },
        { id: 'tab-connections', label: 'Связи', component: Connections },
        { id: 'tab-trademarks', label: 'Товарные знаки', component: Trademarks },
        { id: 'tab-founders', label: 'Учредители', component: Founders },
        { id: 'tab-reliability', label: 'Надежность', component: Analyze },
        { id: 'tab-debts', label: 'Долги', component: Debts },
        { id: 'tab-contracts', label: 'Контракты', component: Contracts },
        { id: 'tab-sanctions', label: 'Санкции', component: Sanctions },
        { id: 'tab-leasing', label: 'Лизинг', component: Leasing },
        { id: 'tab-inspections', label: 'Проверки', component: Inspections },
        { id: 'tab-history', label: 'История', component: History },
        { id: 'tab-details', label: 'Реквизиты', component: Details },
        { id: 'tab-okved', label: 'Виды деятельности', component: Okved },
        { id: 'tab-licenses', label: 'Лицензии', component: Licenses },
        { id: 'tab-branches', label: 'Филиалы', component: Branches },
      ];
      const store = useStore();
      const route = useRoute();
      const isLoading = ref(false);
      const isUpdating = ref(false);
      const accessDenied = ref(false);
      const accessDeniedStatus = ref<number | null>(null);
      const isFriend = ref(false);
      const company = ref<Company | Record<string, any> | null>(null);
      const companyPreview = ref<Record<string, any> | null>(null);
      const companyFinances = ref<CompanyFinances | Record<string, any>>({});
      const companyConnections = ref<CompanyConnection[]>([]);
      const companyFounders = ref<any[]>([]);
      const companyLegalCases = ref<LegalCase[]>([]);
      const companyContracts = ref<Contract[]>([]);
      const companyInspections = ref<Inspection[]>([]);
      const companyEnforcements = ref<Enforcement[]>([]);
      const companyTimeline = ref<TimelineEvent[]>([]);
      const companyFedresurs = ref<CheckoMessage[]>([]);
      const companyBankruptcyMessages = ref<CheckoMessage[]>([]);
      const isLoggedIn = computed(() => store.state.auth.status.loggedIn);
      const activeTab = ref('tab-summary' + route.params.id);
      const currentUser = computed(() => store.state.user.user);
      const isMobileViewport = ref(false);
      const isChina = computed(() => {
        return !!(company.value && company.value.company_name_en && company.value.uscc && !company.value.inn);
      });
      const isEntrepreneur = computed(() => {
        return !!(company.value && company.value.fio && !company.value.name && !company.value.company_name_en);
      });
      const displayName = computed(
        () => company.value?.name || company.value?.fio || company.value?.company_name_en || 'Без названия'
      );
      const canRefreshFromChecko = computed(() => !!company.value?.inn && !isEntrepreneur.value && !isChina.value);
      const accessDeniedTitle = computed(() =>
        accessDeniedStatus.value === 401 ? 'Войдите для просмотра карточки' : 'Оплатите тариф, чтобы увидеть данные'
      );
      const accessDeniedDescription = computed(() =>
        accessDeniedStatus.value === 401
          ? 'Компания найдена. Войдите в аккаунт и оформите тариф, чтобы открыть реквизиты, финансы, связи и остальные разделы.'
          : 'Компания найдена. Подключите оплаченный тариф, чтобы увидеть всю информацию: реквизиты, финансы, связи, арбитраж, долги и историю изменений.'
      );
      const paywallFeatures = ['Сводка', 'Реквизиты', 'Финансы', 'Связи', 'Арбитраж', 'История изменений'];

      const previewName = computed(
        () =>
          companyPreview.value?.name || companyPreview.value?.fio || companyPreview.value?.company_name_en || 'Компания'
      );
      const previewStatus = computed(() => companyPreview.value?.status || '');
      const previewMeta = computed(() => {
        const meta: string[] = [];
        const preview = companyPreview.value;
        if (!preview) return meta;

        if (preview.inn) {
          meta.push(`ИНН ${preview.inn}`);
        }
        if (preview.ogrn) {
          meta.push(`${preview.isEntrepreneur ? 'ОГРНИП' : 'ОГРН'} ${preview.ogrn}`);
        }
        if (preview.uscc) {
          meta.push(`USCC ${preview.uscc}`);
        }

        return meta;
      });

      const getClass = (status: string) => {
        if (status === 'Действует' || status === 'Действующее') {
          return 'active';
        } else if (status === 'Не действует' || status === 'Прекращено') {
          return 'inactive';
        } else {
          return 'liquidated';
        }
      };

      const currentId = computed(() => {
        return company.value?.inn || company.value?.uscc || company.value?.company_name_en || route.params.id;
      });

      function setActiveTab(tabId: string) {
        if (!currentId.value) return;

        closeDropdown();
        activeTab.value = tabId + currentId.value;

        nextTick(() => {
          // Manually activate the correct pane (works for both visible tabs and "Еще" dropdown)
          const allPanes = document.querySelectorAll<HTMLElement>('.counterparties .tab-content > .tab-pane');
          allPanes.forEach((pane) => pane.classList.remove('show', 'active'));
          const targetPane = document.getElementById(tabId + String(currentId.value));
          if (targetPane) {
            targetPane.classList.add('show', 'active');
          }
          // Also mark the corresponding nav link as active via Bootstrap if it exists
          const navEl = document.querySelector<HTMLElement>(`#${tabId}-tab${currentId.value}`);
          if (navEl && navEl instanceof HTMLAnchorElement) {
            try {
              new Tab(navEl).show();
            } catch {
              // ignore
            }
          }
        });
      }

      const isDropdownOpen = ref(false);
      const moreMenuRef = ref<HTMLElement | null>(null);
      const moreToggleRef = ref<HTMLElement | null>(null);
      const dropdownMenuStyle = ref<Record<string, string>>({});
      const visibleTabsLimit = computed(() => (isMobileViewport.value ? 3 : 9));
      const visibleTabs = computed(() => availableTabs.value.slice(0, visibleTabsLimit.value));
      const hiddenItems = computed(() => {
        return availableTabs.value.length > visibleTabsLimit.value
          ? availableTabs.value.slice(visibleTabsLimit.value)
          : [];
      });

      function closeDropdown() {
        isDropdownOpen.value = false;
      }

      const updateDropdownPosition = () => {
        if (!moreToggleRef.value) return;
        const rect = moreToggleRef.value.getBoundingClientRect();

        if (isMobileViewport.value) {
          dropdownMenuStyle.value = {
            position: 'fixed',
            top: `${rect.bottom + 8}px`,
            left: '1rem',
            right: '1rem',
          };
          return;
        }

        dropdownMenuStyle.value = {
          position: 'fixed',
          top: `${rect.bottom + 8}px`,
          left: `${Math.max(16, rect.right - 240)}px`,
          width: '240px',
        };
      };

      function toggleDropdown() {
        isDropdownOpen.value = !isDropdownOpen.value;
        if (isDropdownOpen.value) {
          nextTick(() => updateDropdownPosition());
        }
      }

      const handleDocumentClick = (event: MouseEvent) => {
        const target = event.target as Node | null;
        if (moreMenuRef.value && target && !moreMenuRef.value.contains(target)) {
          closeDropdown();
        }
      };
      const syncViewport = () => {
        isMobileViewport.value = window.innerWidth <= 768;
      };

      const openAuthModal = () => {
        const authModal = document.getElementById('authModal');
        if (authModal) {
          new Modal(authModal).show();
        }
      };

      onMounted(() => {
        syncViewport();
        window.addEventListener('resize', syncViewport);
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);
        document.addEventListener('click', handleDocumentClick);
      });

      onBeforeUnmount(() => {
        window.removeEventListener('resize', syncViewport);
        window.removeEventListener('resize', updateDropdownPosition);
        window.removeEventListener('scroll', updateDropdownPosition, true);
        document.removeEventListener('click', handleDocumentClick);
      });

      const fetchData = async (id: any) => {
        isLoading.value = true;
        accessDenied.value = false;
        accessDeniedStatus.value = null;
        company.value = null;
        companyPreview.value = null;
        companyFounders.value = [];

        const urlQuery = route.query.q;
        const normalizeSearchResult = (searchRes: any, identifier: string) => {
          if (!searchRes) return null;

          if (Array.isArray(searchRes)) {
            return searchRes[0] || null;
          }

          if (searchRes.uscc || searchRes.company_name_en) {
            return searchRes;
          }

          if (Array.isArray(searchRes.org) || Array.isArray(searchRes.ent)) {
            const org = Array.isArray(searchRes.org) ? searchRes.org : [];
            const ent = Array.isArray(searchRes.ent) ? searchRes.ent : [];
            const all = [...org, ...ent];

            return (
              all.find(
                (item: any) =>
                  item?.['ИНН'] === identifier ||
                  item?.['ОГРН'] === identifier ||
                  item?.['ОГРНИП'] === identifier ||
                  item?.['uscc'] === identifier
              ) ||
              all[0] ||
              null
            );
          }

          return null;
        };

        const getStatusName = (value: any) => {
          if (!value) return '';
          if (typeof value === 'string') return value;
          return value.name || value['Наим'] || value['Статус'] || '';
        };

        const normalizePreviewCompany = (result: any) => {
          if (!result) return null;

          return {
            name: result.name || result['НаимСокр'] || result['НаимПолн'] || result.company_name_en || '',
            fio: result.fio || result['ФИО'] || '',
            company_name_en: result.company_name_en || '',
            inn: result.inn || result['ИНН'] || '',
            ogrn: result.ogrn || result['ОГРН'] || result['ОГРНИП'] || '',
            okpo: result.okpo || result['ОКПО'] || '',
            uscc: result.uscc || '',
            status: getStatusName(result.status || result['Статус']),
            isEntrepreneur: Boolean(result.fio || result['ФИО'] || result['ОГРНИП']),
          };
        };

        const loadCompanyPreview = async (identifier: string) => {
          try {
            const searchQuery = String(urlQuery || identifier || '').trim();
            if (!searchQuery) return null;

            const searchRes = await store.dispatch('companies/searchCompanies', searchQuery);
            const result = normalizeSearchResult(searchRes, String(identifier));
            return normalizePreviewCompany(result);
          } catch (_previewError) {
            return null;
          }
        };

        const loadCompanyCollections = async (identifier: string) => {
          companyFinances.value = await store.dispatch('companies/getFinances', identifier).catch(() => ({}));
          await sortFinances();
          companyConnections.value = await store.dispatch('companies/getConnections', identifier).catch(() => []);
          companyLegalCases.value = await store.dispatch('companies/getLegalCases', identifier).catch(() => []);
          companyContracts.value = await store.dispatch('companies/getContracts', identifier).catch(() => []);
          companyInspections.value = await store.dispatch('companies/getInspections', identifier).catch(() => []);
          companyEnforcements.value = await store.dispatch('companies/getEnforcements', identifier).catch(() => []);
          companyTimeline.value = await store.dispatch('companies/getTimeline', identifier).catch(() => []);
          companyFedresurs.value = await store.dispatch('companies/getFedresurs', identifier).catch(() => []);
          companyBankruptcyMessages.value = await store
            .dispatch('companies/getBankruptcyMessages', identifier)
            .catch(() => []);
        };

        const loadEntrepreneurCollections = async (identifier: string) => {
          companyFinances.value = {};
          companyConnections.value = [];
          companyLegalCases.value = [];
          companyContracts.value = [];
          companyInspections.value = [];
          companyEnforcements.value = [];
          companyTimeline.value = await store.dispatch('companies/getTimeline', identifier).catch(() => []);
          companyFedresurs.value = await store.dispatch('companies/getFedresurs', identifier).catch(() => []);
          companyBankruptcyMessages.value = await store
            .dispatch('companies/getBankruptcyMessages', identifier)
            .catch(() => []);
        };

        const resetCompanyCollections = () => {
          companyFinances.value = {};
          companyConnections.value = [];
          companyLegalCases.value = [];
          companyContracts.value = [];
          companyInspections.value = [];
          companyEnforcements.value = [];
          companyTimeline.value = [];
          companyFedresurs.value = [];
          companyBankruptcyMessages.value = [];
        };

        const getErrorStatus = (error: any) => error?.response?.status;

        if (id) {
          try {
            let companyRes = await store.dispatch('companies/getByInn', id);

            if (companyRes) {
              company.value = companyRes;
              setActiveTab('tab-summary');
              await loadCompanyCollections(id);
              if (isLoggedIn.value) {
                CompanyService.getFriends()
                  .then((res) => {
                    res.forEach((item: any) => {
                      if (item?.friend?.id == company.value?.representative) {
                        isFriend.value = true;
                      }
                    });
                  })
                  .catch(() => undefined);
              }
            } else {
              companyRes = await store.dispatch('companies/getByIP', id);
              if (companyRes && typeof companyRes === 'object') {
                company.value = companyRes;
                setActiveTab('tab-summary');
                await loadEntrepreneurCollections(id);
              }
            }

            if (!companyRes || typeof companyRes !== 'object') {
              throw new Error('Primary company endpoints returned empty response');
            }
            if (companyRes.founders && companyRes.founders?.length) {
              companyFounders.value = [...companyRes.founders[0]?.PP, ...companyRes.founders[0]?.LP] || [];
            } else if (companyRes.founder_organizations && companyRes.founder_organizations.length) {
              companyFounders.value = companyRes.founder_organizations[0]?.PP || [];
            }
          } catch (_error) {
            const status = getErrorStatus(_error);
            if (status === 401 || status === 403) {
              accessDenied.value = true;
              accessDeniedStatus.value = status;
              company.value = null;
              companyPreview.value = await loadCompanyPreview(id);
              resetCompanyCollections();
              return;
            }

            try {
              const searchQuery = urlQuery || id.trim();
              const searchRes = await store.dispatch('companies/searchCompanies', searchQuery);
              const result = normalizeSearchResult(searchRes, id);

              if (result) {
                company.value = result;
                setActiveTab('tab-summary');
                if (result.company_name_en) {
                  resetCompanyCollections();
                }
              } else {
                company.value = null;
              }
            } catch (_nestedError) {
              const nestedStatus = getErrorStatus(_nestedError);
              if (nestedStatus === 401 || nestedStatus === 403) {
                accessDenied.value = true;
                accessDeniedStatus.value = nestedStatus;
                companyPreview.value = await loadCompanyPreview(id);
                resetCompanyCollections();
                return;
              }
              company.value = null;
            }
          } finally {
            isLoading.value = false;
          }
        } else {
          isLoading.value = false;
        }
      };

      const addFriend = () => {
        const form = ref({
          friend: company.value?.representative,
        });
        CompanyService.addFriend(form.value).then(() => {
          isFriend.value = true;
          alert('Пользователь добавлен в друзья');
        });
      };

      const sortFinances = () => {
        companyFinances.value.finances_years?.sort((a: any, b: any) => Number(a.year) - Number(b.year));
      };

      const availableTabs = computed(() => {
        if (isChina.value) {
          return tabs.filter((tab) => tab.id === 'tab-summary');
        }

        if (isEntrepreneur.value) {
          return tabs.filter((tab) =>
            [
              'tab-summary',
              'tab-founders',
              'tab-trademarks',
              'tab-history',
              'tab-details',
              'tab-okved',
              'tab-licenses',
            ].includes(tab.id)
          );
        }

        return tabs;
      });

      // Функция для скачивания файла
      const downloadCompanyInfo = () => {
        const companyData = (company.value ?? {}) as any;

        // Добавляем общую информацию о компании (название, ИНН, адрес и т.д.)
        const companyInfo = [
          ['Полное наименование', companyData.name_full],
          ['Сокращенное наименование', companyData.name],
          ['Юридический адрес', companyData.legal_adress?.adress],
          ['Email', companyData.contacts?.email?.[0] || '-'],
          ['Телефон/факс', companyData.contacts?.phone?.[0] || companyData.contacts?.fax?.[0] || '-'],
          ['ИНН/КПП', companyData.inn],
          ['ОГРН', companyData.ogrn],
          ['ОКПО', companyData.okpo?.code || companyData.okpo],
          ['ОКАТО', companyData.okato?.code],
          ['ОКТМО', companyData.oktmo?.code],
          ['ОКОГУ', companyData.okogu?.code],
          ['ОКФС', companyData.okfs?.code],
          ['ОКОПФ', companyData.okopf?.code],
          ['Директор', companyData.directors?.[0]?.fio],
        ];

        // Преобразуем данные в лист Excel
        const worksheet = XLSX.utils.aoa_to_sheet(companyInfo);
        const rowHeights = new Array(companyInfo.length).fill({ hpt: 40 });
        worksheet['!rows'] = rowHeights;
        // Настройка ширины колонок
        worksheet['!cols'] = [
          { wch: 40 }, // ширина первой колонки
          { wch: 60 }, // ширина второй колонки
        ];

        // Применение стиля к заголовку
        worksheet['A1'].s = {
          font: { bold: true, sz: 16 },
          alignment: { horizontal: 'center' },
        };
        // Применение жирного текста для первого столбца
        for (let i = 3; i <= companyInfo.length + 1; i++) {
          const cell = `A${i}`;
          if (worksheet[cell]) {
            worksheet[cell].s = {
              font: { bold: true }, // Делает текст жирным
              alignment: { wrapText: true }, // Авто-перенос текста
            };
          }
        }
        // Применение авто-переноса текста
        for (let cell in worksheet) {
          if (cell[0] !== '!') {
            // Исключаем специальные параметры, такие как "!ref"
            worksheet[cell].s = { alignment: { wrapText: true } };
          }
        }
        worksheet['!ref'] = `A1:B15`; // Заменяем жесткое значение на динамическое

        // Создаём новую книгу Excel
        const workbook = XLSX.utils.book_new();
        // Добавляем лист в книгу
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Company Info');

        // Сохраняем файл
        XLSX.writeFile(workbook, 'Карточка_' + (companyData.name || 'company') + '.xlsx');
      };

      const updateCompanyData = async () => {
        const inn = company.value?.inn;
        if (!inn) return;

        isUpdating.value = true;
        try {
          await store.dispatch('companies/updateCompanyData', inn);
          store.dispatch('notifications/triggerToast', {
            message: 'Данные компании успешно обновлены',
            type: 'success',
          });

          await fetchData(inn);
        } catch (_error) {
          store.dispatch('notifications/triggerToast', {
            message: 'Не удалось обновить данные компании',
            type: 'error',
          });
        } finally {
          isUpdating.value = false;
        }
      };

      watch(
        () => [route.params.id, route.query.q],
        ([newId]) => {
          fetchData(newId);
        },
        { immediate: true }
      );

      return {
        company,
        companyPreview,
        accessDenied,
        accessDeniedStatus,
        accessDeniedTitle,
        accessDeniedDescription,
        paywallFeatures,
        previewName,
        previewStatus,
        previewMeta,
        companyFounders,
        companyFinances,
        companyConnections,
        companyLegalCases,
        companyContracts,
        companyInspections,
        companyEnforcements,
        companyTimeline,
        companyFedresurs,
        companyBankruptcyMessages,
        tabs,
        availableTabs,
        visibleTabs,
        hiddenItems,
        isDropdownOpen,
        moreMenuRef,
        moreToggleRef,
        dropdownMenuStyle,
        route,
        activeTab,
        isLoggedIn,
        isFriend,
        isLoading,
        isUpdating,
        currentUser,
        displayName,
        canRefreshFromChecko,
        isEntrepreneur,
        closeDropdown,
        setActiveTab,
        getClass,
        toggleDropdown,
        addFriend,
        downloadCompanyInfo,
        updateCompanyData,
        openAuthModal,
        currentId,
      };
    },
  });
